import { API } from '../api.js';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = ['Tech','Money','Health','Self-improvement','Hobbies','Music','Sports','Spirituality','Education','Business','Art','Gaming','Other'];
const CATEGORY_EMOJI = { Tech:'💻', Money:'💰', Health:'🥗', 'Self-improvement':'📈', Hobbies:'🎸', Music:'🎵', Sports:'⚽', Spirituality:'🧘', Education:'📚', Business:'💼', Art:'🎨', Gaming:'🎮', Other:'🌐' };
const COLORS = ['#0f172a','#1e3a5f','#14532d','#4a1d96','#7c2d12','#1e1b4b','#164e63','#831843'];
const EMOJIS = ['🎓','🤖','💡','🚀','💎','🔥','⚡','🌟','🎯','💰','🏆','📚','🎸','⚽','🧘','💻'];

const STEPS = [
  { id:1, title:'Name your community', sub:'Choose a name and URL for your community' },
  { id:2, title:'Describe it', sub:'Tell people what your community is about' },
  { id:3, title:'Set pricing', sub:'Choose how members access your community' },
  { id:4, title:'Customize', sub:'Pick an emoji and color for your community' },
];

export default function CreateCommunityPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [step, setStep]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const [form, setForm] = useState({
    name:       '',
    slug:       '',
    category:   'Tech',
    description:'',
    pricing:    'free',
    price:      '',
    is_private: false,
    emoji:      '🎓',
    color:      '#0f172a',
  });

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function slugify(str) {
    return str.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 40);
  }

  function handleNameChange(val) {
    set('name', val);
    if (!form.slug || form.slug === slugify(form.name)) {
      set('slug', slugify(val));
    }
  }

  function canNext() {
    if (step === 1) return form.name.trim().length >= 3 && form.slug.trim().length >= 2;
    if (step === 2) return form.description.trim().length >= 10;
    if (step === 3) return form.pricing === 'free' || (form.pricing === 'paid' && parseFloat(form.price) > 0);
    return true;
  }

  async function handleSubmit() {
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API + '/api/communities', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          name: form.name,
          slug: form.slug,
          category: form.category,
          description: form.description,
          isPrivate: form.is_private,
          emoji: form.emoji,
          color: form.color,
          pricing: form.pricing,
          price: parseFloat(form.price) || 0
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create community');
      const createdSlug = data.slug || form.slug;
      navigate(createdSlug ? `/${createdSlug}/community` : '/app');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  const progress = (step / STEPS.length) * 100;

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', flexDirection:'column', fontFamily:'inherit' }}>

      {/* ── Header ── */}
      <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'var(--sat) 24px 0', height:'calc(60px + var(--sat))', display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontWeight:800, fontSize:18 }} onClick={() => navigate('/')}>
          <div style={{ width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
            <img src="/logo2.png" alt="Qeduvo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          Qeduvo
        </div>
        <div style={{ flex:1 }} />
        <button onClick={() => navigate(-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:13, fontWeight:600 }}>✕ Cancel</button>
      </div>

      {/* ── Progress bar ── */}
      <div style={{ height:3, background:'var(--border)' }}>
        <div style={{ height:'100%', width:progress+'%', background:'var(--accent)', transition:'width 0.4s' }} />
      </div>

      {/* ── Main content ── */}
      <div style={{ flex:1, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'60px 24px' }}>
        <div style={{ width:'100%', maxWidth:560 }}>

          {/* Step indicator */}
          <div style={{ display:'flex', gap:8, marginBottom:32 }}>
            {STEPS.map(s => (
              <div key={s.id} style={{ display:'flex', alignItems:'center', gap:6, opacity: s.id <= step ? 1 : 0.3 }}>
                <div style={{ width:24, height:24, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800,
                  background: s.id < step ? 'var(--accent3)' : s.id === step ? 'var(--accent)' : 'var(--surface2)',
                  color: s.id <= step ? '#fff' : 'var(--text3)' }}>
                  {s.id < step ? '✓' : s.id}
                </div>
                {s.id < STEPS.length && <div style={{ width:32, height:2, background: s.id < step ? 'var(--accent3)' : 'var(--border)' }} />}
              </div>
            ))}
          </div>

          <h1 style={{ fontSize:30, fontWeight:900, marginBottom:6, letterSpacing:-0.5 }}>{STEPS[step-1].title}</h1>
          <p style={{ fontSize:15, color:'var(--text3)', marginBottom:36 }}>{STEPS[step-1].sub}</p>

          {error && <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid var(--accent2)', color:'var(--accent2)', borderRadius:10, padding:'12px 16px', marginBottom:24, fontSize:14 }}>{error}</div>}

          {/* ── STEP 1: Name + Slug + Category ── */}
          {step === 1 && (
            <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:8 }}>Community name *</label>
                <input value={form.name} onChange={e => handleNameChange(e.target.value)}
                  placeholder="e.g. AI Money Lab" maxLength={60}
                  style={{ width:'100%', padding:'13px 16px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', fontSize:16, outline:'none', fontFamily:'inherit' }} />
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:5, textAlign:'right' }}>{form.name.length}/60</div>
              </div>

              <div>
                <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:8 }}>Community URL *</label>
                <div style={{ display:'flex', alignItems:'center', border:'1px solid var(--border)', borderRadius:10, background:'var(--surface2)', overflow:'hidden' }}>
                  <span style={{ padding:'13px 12px 13px 16px', fontSize:14, color:'var(--text3)', whiteSpace:'nowrap', borderRight:'1px solid var(--border)', background:'var(--surface3)' }}>
                    qeduvo.com/
                  </span>
                  <input value={form.slug} onChange={e => set('slug', slugify(e.target.value))}
                    placeholder="your-community" maxLength={40}
                    style={{ flex:1, padding:'13px 16px', border:'none', background:'transparent', color:'var(--text)', fontSize:14, outline:'none', fontFamily:'inherit' }} />
                </div>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:5 }}>Only lowercase letters, numbers, and hyphens</div>
              </div>

              <div>
                <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:10 }}>Category *</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {CATEGORIES.map(cat => (
                    <button key={cat} onClick={() => set('category', cat)}
                      style={{ padding:'8px 14px', borderRadius:20, border:'1px solid', cursor:'pointer', fontSize:13, fontWeight:600, transition:'all 0.15s',
                        borderColor: form.category === cat ? 'var(--accent)' : 'var(--border)',
                        background: form.category === cat ? 'rgba(99,91,255,0.1)' : 'var(--surface2)',
                        color: form.category === cat ? 'var(--accent)' : 'var(--text2)' }}>
                      {CATEGORY_EMOJI[cat]} {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:10 }}>Access type</label>
                <div className="form-row-2">
                  {[
                    { val:false, icon:'🌍', title:'Public', desc:'Anyone can discover and join' },
                    { val:true,  icon:'🔒', title:'Private', desc:'Members only — invite required' },
                  ].map(opt => (
                    <div key={opt.title} onClick={() => set('is_private', opt.val)}
                      style={{ padding:'16px', borderRadius:10, border:'2px solid', cursor:'pointer',
                        borderColor: form.is_private === opt.val ? 'var(--accent)' : 'var(--border)',
                        background: form.is_private === opt.val ? 'rgba(99,91,255,0.05)' : 'var(--surface2)' }}>
                      <div style={{ fontSize:22, marginBottom:6 }}>{opt.icon}</div>
                      <div style={{ fontWeight:700, fontSize:14 }}>{opt.title}</div>
                      <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: Description ── */}
          {step === 2 && (
            <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:8 }}>Description *</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)}
                  placeholder="Tell potential members what your community is about, what they'll learn, and why they should join..."
                  maxLength={500} rows={6}
                  style={{ width:'100%', padding:'13px 16px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', fontSize:14, outline:'none', fontFamily:'inherit', resize:'vertical', lineHeight:1.6 }} />
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:5, textAlign:'right' }}>{form.description.length}/500</div>
              </div>

              {/* Preview card */}
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--text3)', padding:'10px 16px', background:'var(--surface2)', textTransform:'uppercase', letterSpacing:1 }}>Preview</div>
                <div style={{ height:90, background: 'linear-gradient(135deg, ' + form.color + ', ' + form.color + '88)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:36 }}>
                  {form.emoji}
                </div>
                <div style={{ padding:'0 16px 16px' }}>
                  <div style={{ fontWeight:700, fontSize:16, marginBottom:4, marginTop:8 }}>{form.name || 'Community Name'}</div>
                  <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.5 }}>{form.description || 'Your description will appear here.'}</div>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: Pricing ── */}
          {step === 3 && (
            <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                {[
                  { val:'free',  icon:'🎁', title:'Free', desc:'Members join for free. Monetize via courses, affiliates, or upsells.' },
                  { val:'paid',  icon:'💎', title:'Paid membership', desc:'Charge a monthly or one-time fee to access your community.' },
                ].map(opt => (
                  <div key={opt.val} onClick={() => set('pricing', opt.val)}
                    style={{ padding:'20px', borderRadius:12, border:'2px solid', cursor:'pointer', display:'flex', gap:16, alignItems:'flex-start',
                      borderColor: form.pricing === opt.val ? 'var(--accent)' : 'var(--border)',
                      background: form.pricing === opt.val ? 'rgba(99,91,255,0.06)' : 'var(--surface2)' }}>
                    <div style={{ width:44, height:44, borderRadius:12, background: form.pricing === opt.val ? 'rgba(99,91,255,0.15)' : 'var(--surface3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                      {opt.icon}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:15, marginBottom:4, display:'flex', alignItems:'center', gap:8 }}>
                        {opt.title}
                        {form.pricing === opt.val && <span style={{ fontSize:12, background:'var(--accent)', color:'#fff', borderRadius:10, padding:'2px 8px' }}>Selected</span>}
                      </div>
                      <div style={{ fontSize:13, color:'var(--text3)' }}>{opt.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {form.pricing === 'paid' && (
                <div style={{ background:'var(--surface2)', borderRadius:12, padding:20, border:'1px solid var(--border)' }}>
                  <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:10 }}>Monthly price (USD)</label>
                  <div style={{ display:'flex', alignItems:'center', border:'1px solid var(--border)', borderRadius:10, background:'var(--surface)', overflow:'hidden' }}>
                    <span style={{ padding:'12px 14px', fontSize:16, color:'var(--text3)', borderRight:'1px solid var(--border)', background:'var(--surface2)' }}>$</span>
                    <input type="number" value={form.price} onChange={e => set('price', e.target.value)}
                      placeholder="9" min="1" max="9999" step="1"
                      style={{ flex:1, padding:'12px 16px', border:'none', background:'transparent', color:'var(--text)', fontSize:18, fontWeight:700, outline:'none', fontFamily:'inherit' }} />
                    <span style={{ padding:'12px 14px', fontSize:13, color:'var(--text3)' }}>/month</span>
                  </div>
                  <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>You keep 97% of revenue after payment processing fees</div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 4: Emoji + Color ── */}
          {step === 4 && (
            <div style={{ display:'flex', flexDirection:'column', gap:28 }}>
              <div>
                <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:12 }}>Choose an emoji</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                  {EMOJIS.map(em => (
                    <button key={em} onClick={() => set('emoji', em)}
                      style={{ width:52, height:52, borderRadius:12, border:'2px solid', cursor:'pointer', fontSize:26, display:'flex', alignItems:'center', justifyContent:'center',
                        borderColor: form.emoji === em ? 'var(--accent)' : 'var(--border)',
                        background: form.emoji === em ? 'rgba(99,91,255,0.1)' : 'var(--surface2)' }}>
                      {em}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize:13, fontWeight:700, color:'var(--text)', display:'block', marginBottom:12 }}>Banner color</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                  {COLORS.map(c => (
                    <button key={c} onClick={() => set('color', c)}
                      style={{ width:44, height:44, borderRadius:10, border:'3px solid', cursor:'pointer', background:c,
                        borderColor: form.color === c ? 'var(--accent)' : 'transparent' }} />
                  ))}
                </div>
              </div>

              {/* Final preview */}
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden', boxShadow:'var(--shadow)' }}>
                <div style={{ height:120, background:'linear-gradient(135deg, '+form.color+', '+form.color+'66)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:52 }}>
                  {form.emoji}
                </div>
                <div style={{ padding:'0 20px 20px', position:'relative' }}>
                  <div style={{ width:56, height:56, borderRadius:10, background:form.color, border:'3px solid var(--surface)', marginTop:-28, marginBottom:12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28 }}>
                    {form.emoji}
                  </div>
                  <div style={{ fontWeight:800, fontSize:18, marginBottom:4 }}>{form.name || 'Community Name'}</div>
                  <div style={{ fontSize:12, color:'var(--text3)', marginBottom:8 }}>qeduvo.com/{form.slug || 'your-community'}</div>
                  <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.5 }}>{form.description || 'Your description here.'}</div>
                  <div style={{ display:'flex', gap:8, marginTop:12 }}>
                    <span style={{ fontSize:11, fontWeight:600, background:'var(--surface2)', borderRadius:20, padding:'3px 10px' }}>👥 1 Member</span>
                    <span style={{ fontSize:11, fontWeight:600, borderRadius:20, padding:'3px 10px',
                      background: form.pricing === 'free' ? 'rgba(16,185,129,0.1)' : 'rgba(99,91,255,0.1)',
                      color: form.pricing === 'free' ? 'var(--accent3)' : 'var(--accent)' }}>
                      {form.pricing === 'free' ? 'Free' : '$'+form.price+'/month'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Navigation buttons ── */}
          <div style={{ display:'flex', gap:12, marginTop:40 }}>
            {step > 1 && (
              <button onClick={() => setStep(s => s-1)}
                style={{ flex:1, padding:'14px', borderRadius:10, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', cursor:'pointer', fontSize:15, fontWeight:700 }}>
                ← Back
              </button>
            )}
            {step < 4 ? (
              <button onClick={() => { if (canNext()) setStep(s => s+1); }} disabled={!canNext()}
                style={{ flex:2, padding:'14px', borderRadius:10, border:'none', cursor: canNext() ? 'pointer' : 'not-allowed', fontSize:15, fontWeight:800,
                  background: canNext() ? 'var(--accent)' : 'var(--surface3)',
                  color: canNext() ? '#fff' : 'var(--text3)' }}>
                Continue →
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading || !canNext()}
                style={{ flex:2, padding:'14px', borderRadius:10, border:'none', background: loading ? 'var(--surface3)' : 'var(--accent)', color: loading ? 'var(--text3)' : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontSize:15, fontWeight:800 }}>
                {loading ? 'Creating…' : '🚀 Create Community'}
              </button>
            )}
          </div>

          <div style={{ fontSize:12, color:'var(--text3)', textAlign:'center', marginTop:16 }}>
            {user ? `Creating as ${user.display_name}` : <span onClick={() => navigate('/login')} style={{ color:'var(--accent)', cursor:'pointer' }}>Log in to create a community</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
